import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EntitlementDisabledTooltip } from '@features/entitlements';
import {
  Add as AddIcon,
  Description as TemplateIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useOrgTemplates } from '../hooks/useOrgTemplates';
import { useUpdateTemplateMutation } from '../store';
import type { OrgNoteTemplateInfo } from '../types';

interface OrgTemplatesSectionProps {
  /** Optional callback when a template is selected (for use within TemplatesPage) */
  onSelectTemplate?: (templateId: string) => void;
}

export const OrgTemplatesSection: React.FC<OrgTemplatesSectionProps> = ({
  onSelectTemplate,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { templates, isLoading, isOrgAdmin, createOrgTemplate, refetch } = useOrgTemplates();
  const [updateTemplateMutation] = useUpdateTemplateMutation();
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateNew = useCallback(async () => {
    setIsCreating(true);
    // Create a basic template and navigate to editor
    const templateId = await createOrgTemplate({
      title: 'New Organization Template',
      description: 'Template for your organization',
      sections: [],
    });
    
    if (templateId) {
      if (onSelectTemplate) {
        onSelectTemplate(templateId);
      } else {
        navigate(`/templates?id=${templateId}`);
      }
    }
    setIsCreating(false);
  }, [createOrgTemplate, navigate, onSelectTemplate]);

  const handleToggleLock = useCallback(async (template: OrgNoteTemplateInfo, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click navigation
    
    const currentAllowForks = template.allow_user_forks ?? true;
    const newAllowForks = !currentAllowForks;

    try {
      await updateTemplateMutation({
        templateId: template.id,
        updateNoteTemplateRequest: {
          allow_user_forks: newAllowForks,
        },
      }).unwrap();

      enqueueSnackbar(
        newAllowForks
          ? 'Template unlocked - members can now customize'
          : 'Template locked - members cannot customize',
        { variant: 'success' },
      );

      // Refresh the template list to reflect the new lock state
      refetch();
    } catch (error) {
      console.error('Failed to toggle template lock:', error);
      enqueueSnackbar('Failed to update template settings', { variant: 'error' });
    }
  }, [updateTemplateMutation, enqueueSnackbar, refetch]);

  if (!isOrgAdmin) {
    return null;
  }

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card elevation={1} sx={{ mb: 3, borderRadius: 2, border: 1, borderColor: 'grey.200' }}>
      <CardHeader
        title="Organization Templates"
        subheader="Manage templates available to everyone in your organization"
        action={
          <EntitlementDisabledTooltip capabilityName="template_creation">
            <Button
              variant="contained"
              startIcon={isCreating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={handleCreateNew}
              disabled={isCreating}
            >
              Create Org Template
            </Button>
          </EntitlementDisabledTooltip>
        }
      />
      <CardContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : templates.length === 0 ? (
          <Alert severity="info" variant="outlined">
            Your organization doesn't have any templates yet. You can create one or convert your personal templates to organization scope.
          </Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Template Name</TableCell>
                  <TableCell>Version</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Modified</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template: OrgNoteTemplateInfo) => {
                  const isLocked = !(template.allow_user_forks ?? true);
                  return (
                    <TableRow 
                      key={template.id} 
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (onSelectTemplate) {
                          onSelectTemplate(template.id);
                        } else {
                          navigate(`/templates?id=${template.id}`);
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <TemplateIcon color="action" fontSize="small" />
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {template.title}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={`v${template.current_version}`} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(template.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(template.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title={isLocked ? 'Template locked - members cannot customize' : 'Template unlocked - members can customize'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleToggleLock(template, e)}
                              sx={{ color: isLocked ? 'text.secondary' : 'primary.main' }}
                            >
                              {isLocked ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Template">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onSelectTemplate) {
                                  onSelectTemplate(template.id);
                                } else {
                                  navigate(`/templates?id=${template.id}`);
                                }
                              }}
                            >
                              <SettingsIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

